/**
 * Global axios response interceptor.
 *
 * Purpose: Convert backend 403 responses from edit endpoints (which a
 * viewer hits when they try to mutate anything) into a friendly
 * "Viewer mode only" sonner toast — INSTEAD of letting raw errors
 * bubble up and crash the component tree.
 *
 * Behaviour:
 *   - On 403, fire ONE toast at a time (3-second throttle so we don't
 *     stack multiple toasts when a single user action triggers a burst
 *     of edit calls — e.g., a multi-field save).
 *   - The promise is STILL rejected so each component's local catch
 *     block still runs (state rollback, etc.). We just attach
 *     `err.viewer_blocked = true` so components can skip their local
 *     alert() if they want a cleaner UX.
 *   - Idempotent: the module installs its interceptor at import time
 *     and tracks installation so re-imports during HMR don't stack
 *     handlers.
 *
 * Imported once from `App.js`.
 */
import axios from 'axios';
import { toast } from 'sonner';

let installed = false;
let lastToastAt = 0;

const THROTTLE_MS = 3000;

function installInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 403) {
        // Mark for downstream catch blocks so they can skip their alert.
        error.viewer_blocked = true;

        const now = Date.now();
        if (now - lastToastAt > THROTTLE_MS) {
          lastToastAt = now;
          toast.warning('Viewer mode only', {
            description:
              'Only editors and admins can make changes here. Ask an admin to upgrade your role from Admin Panel → Users, or request temporary access from the padlock buttons.',
            duration: 4500,
          });
        }
      }
      return Promise.reject(error);
    },
  );

  // Safety net for any other unhandled rejection that would otherwise
  // surface in DevTools as an uncaught error. We don't toast here (the
  // 403 case is handled above); we just prevent the noisy crash.
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (evt) => {
      const reason = evt?.reason;
      if (reason && reason.viewer_blocked) {
        // Already handled above — silence the unhandled-rejection log
        // so the browser console stays clean.
        evt.preventDefault();
      }
    });
  }
}

installInterceptor();

export default installInterceptor;
